import { getAnalyticsInstance } from '@hooks/useAnalytics';
import { ReporterOptions } from '@cvent/sli-nextjs-metrics';
import { ACTION, PAGE_VIEW } from '@cvent/react-analytics';
import { getTransformer, Reporter } from './metricDataTransformers';
import { AnalyticsDestinations } from './types';

const supportedDestinationsSet: Set<string> = new Set([
  AnalyticsDestinations.ANALYTICS,
  AnalyticsDestinations.ATTENDEE_ACTIVITIES
]);

type AnalyticsReporterOptions = ReporterOptions & {
  metadata?: {
    analytics?: {
      id?: string;
      type?: ACTION | PAGE_VIEW;
      path?: string;
    };
  };
};

/**
 * Analytics reporter, if analytics was initialized, it tracks the event using the react-analytics
 * instance which then will use its reporters to send data to the supported destinations:
 *
 *  - Cvent Analytics
 *  - Attendee Activities
 *  - Mixpanel
 *
 *  TODO HUB-100043 to cleanup the extra logic once all the analytics facts are migrated
 */
export const analyticsReporter = (
  id: string,
  data: Record<string, unknown> | undefined = undefined,
  options?: AnalyticsReporterOptions
) => {
  const analytics = getAnalyticsInstance();
  if (analytics) {
    // For now we ignore page_loads and abandoned events to not pollute data in destinations
    // As well as events that results in an error
    // Note: Consider moving the logic to check for errors to each destinations in react-analytics
    if (id === 'page_load' || data.type === 'abandoned' || data.result === 'error') return;

    // Analytics has its own type prop that overrides the type from the page-actions API (i.e. tracked, abandoned, etc)
    // We do not require to pass destinations, attributes, and pageTimings to analytics
    // To avoid duplicate event names, the tracking-plan-publisher will append the plan name to all actions by
    // default to guarantee unique names for each action published to the same workspace in Rudderstack. The API
    // will add the "downstreamName" to the data which we should use as the id/action name.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { destinations, attributes, pageTimings, downstreamName, ...rest } = data;

    // ignore if metric is meant for other reporters
    if (destinations && !(destinations as string[])?.some(destination => supportedDestinationsSet.has(destination))) {
      return;
    }
    const eventName = downstreamName && typeof downstreamName === 'string' ? String(downstreamName) : id;

    // metadata passed in options takes precedence. For "action" types the id is used as the fact identifier
    // whereas for "pageView" types the path is used instead
    const trackData = {
      id: options?.metadata?.analytics?.type === ACTION ? options.metadata.analytics.id : eventName,
      path: options?.metadata?.analytics?.type === PAGE_VIEW ? options.metadata.analytics.path : undefined,
      ...getTransformer(Reporter.ANALYTICS, options?.metadata?.analytics?.id || eventName)(rest),
      metricType: data.type
    };
    analytics.track(trackData);
  }
};
